import { useCallback, useEffect, useRef } from "react";
import type { QueryClient } from "@tanstack/react-query";
import {
  installResumeQueryCachePersistence,
  refreshActiveResumeQueries,
} from "@/lib/query/resumeQueryCache";

interface ResumeQueryCacheLifecycleOptions {
  isAuthenticated: boolean;
  queryClient: QueryClient;
  userId: string | null;
}

export function useResumeQueryCacheLifecycle({
  isAuthenticated,
  queryClient,
  userId,
}: ResumeQueryCacheLifecycleOptions) {
  const refreshInFlightRef = useRef<Promise<void> | null>(null);

  // For a native resume: tabs stay mounted behind display:none, so nothing else
  // reads them again. A cold start doesn't need it: restored queries keep their
  // timestamps, and each query's staleTime decides whether it reads.
  const refreshResumeQueries = useCallback(() => {
    if (!isAuthenticated || !userId) return Promise.resolve();
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const refreshPromise: Promise<void> = refreshActiveResumeQueries(queryClient)
      .finally(() => {
        if (refreshInFlightRef.current === refreshPromise) {
          refreshInFlightRef.current = null;
        }
      });

    refreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [isAuthenticated, queryClient, userId]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      refreshInFlightRef.current = null;
      return;
    }

    const stopPersistence = installResumeQueryCachePersistence(queryClient, userId);

    return () => {
      refreshInFlightRef.current = null;
      stopPersistence();
    };
  }, [isAuthenticated, queryClient, userId]);

  return refreshResumeQueries;
}
